import React, { createContext, useCallback, useContext, useRef } from 'react';
import { IonModal, IonContent } from '@ionic/react';
import {
  INTERSTITIAL_BREAKPOINT,
  INTERSTITIAL_BREAKPOINTS,
  INTERSTITIAL_BREAKPOINT_COMPACT,
  INTERSTITIAL_BREAKPOINTS_COMPACT,
  INTERSTITIAL_BREAKPOINT_TALL,
  INTERSTITIAL_BREAKPOINTS_TALL,
} from '@/modules/config';
import '@/components/Interstitial.css';

/**
 * Expands the sheet to full height and scrolls one of its detail anchors into
 * view. Provided by `WelcomeSheet` so a section deep in the tree can offer an
 * "instructions below" link without the sheet threading a callback to it.
 * Outside a sheet it is a no-op, so a section renders standalone in a test.
 */
const ExpandToAnchorContext = createContext<(anchorId: string) => void>(
  () => {}
);

export const useExpandToAnchor = () => useContext(ExpandToAnchorContext);

interface WelcomeSheetProps {
  isOpen: boolean;
  onDismiss: () => void;
  /**
   * True when the sheet has nothing below the fold, so it opens at the shorter
   * breakpoint. A sheet with a detail zone (the Home-Screen install
   * instructions, a news story) opens taller and can be dragged to full height.
   */
  compact: boolean;
  /**
   * True when the sheet holds something worth reading in place, such as a
   * news story, so it opens to three quarters. Ignored on a compact sheet.
   */
  tall?: boolean;
  children: React.ReactNode;
}

/**
 * The bottom-sheet shell every welcome interstitial renders into: the modal,
 * its breakpoints, and the scroll container the detail anchors live in. Each
 * topic type composes its own content inside it.
 */
const WelcomeSheet: React.FC<WelcomeSheetProps> = ({
  isOpen,
  onDismiss,
  compact,
  tall = false,
  children,
}) => {
  const modalRef = useRef<HTMLIonModalElement>(null);
  const contentRef = useRef<HTMLIonContentElement>(null);

  const expandToAnchor = useCallback(async (anchorId: string) => {
    await modalRef.current?.setCurrentBreakpoint(1);
    setTimeout(() => {
      const el = document.getElementById(anchorId);
      if (el) {
        contentRef.current?.scrollToPoint(0, el.offsetTop - 16, 300);
      }
    }, 300);
  }, []);

  return (
    <IonModal
      ref={modalRef}
      isOpen={isOpen}
      initialBreakpoint={
        compact
          ? INTERSTITIAL_BREAKPOINT_COMPACT
          : tall
            ? INTERSTITIAL_BREAKPOINT_TALL
            : INTERSTITIAL_BREAKPOINT
      }
      breakpoints={
        compact
          ? INTERSTITIAL_BREAKPOINTS_COMPACT
          : tall
            ? INTERSTITIAL_BREAKPOINTS_TALL
            : INTERSTITIAL_BREAKPOINTS
      }
      handle
      onDidDismiss={onDismiss}
    >
      <IonContent ref={contentRef} className="ion-padding-top">
        <ExpandToAnchorContext.Provider value={expandToAnchor}>
          {children}
        </ExpandToAnchorContext.Provider>
      </IonContent>
    </IonModal>
  );
};

export default WelcomeSheet;
